import { useEffect, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { RotateCw } from 'lucide-react'
import ExportIcons from '@/components/shared/ExportIcons'
import type { DashboardBoard } from '@/types/dashboards'
import {
  DashboardCustomizationProvider,
  DashboardCustomizeControl,
} from './DashboardCustomization'
import DrillDownDialog from './DrillDownDialog'
import { panelErrorMessage } from './Panel'

/** hh:mm:ss in the operator's own timezone — boards are read against a clock
 *  on the wall, not against a UTC stamp. */
function clockStamp(at: number): string {
  return new Date(at).toLocaleTimeString('en-US', { hour12: false })
}

interface DashboardShellProps {
  /** Enables per-user layout preferences for this board and active tab. */
  dashboardId?: DashboardBoard
  dashboardView?: string
  title: string
  subtitle?: string
  /** Small mono label above the title, e.g. 'LEGAL & COMPLIANCE'. */
  kicker?: string
  /** From useDashboard — drives the Live chip and the last-updated stamp. */
  dataUpdatedAt?: number
  isFetching?: boolean
  error?: unknown
  onRetry?: () => void
  /** 'live' | 'demo'. Shown so an operator always knows which data they read. */
  partition?: string | null
  /** Filter bar controls rendered between the title block and the chips. */
  filters?: ReactNode
  /** Report template id for the Print / PDF / Excel actions. Omitted = no icons. */
  exportTemplate?: string
  exportFilters?: Record<string, unknown>
  /** Page-level sub-navigation, rendered as one strip below the title bar.
   *  Full width and above the content so it reads as page tabs, not a control
   *  belonging to any one panel. */
  tabs?: ReactNode
  /** Fill mode for tabbed boards: the content area does not scroll — each tab's
   *  4x4 grid fills the viewport exactly and panels scroll inside themselves.
   *  Default (false) keeps the single page scroll for long, non-tabbed boards. */
  fill?: boolean
  children: ReactNode
}

/**
 * Page frame for every board.
 *
 * Mounts the shared drill-down dialog once, so any Figure anywhere inside the
 * board opens records without each page wiring the modal itself.
 *
 * The Live chip counts seconds since the last successful fetch (the boards
 * poll every 30s). It is deliberately an age, not a row count: an operator
 * reading a stale board needs to know the data stopped moving, and age is the
 * only thing that says so.
 */
export default function DashboardShell(props: DashboardShellProps) {
  if (props.dashboardId && props.dashboardView) {
    return (
      <DashboardCustomizationProvider board={props.dashboardId} view={props.dashboardView}>
        <DashboardShellContent {...props} />
      </DashboardCustomizationProvider>
    )
  }
  return <DashboardShellContent {...props} />
}

function DashboardShellContent({
  title,
  subtitle,
  kicker,
  dataUpdatedAt,
  isFetching = false,
  error = null,
  onRetry,
  partition,
  filters,
  exportTemplate,
  exportFilters,
  tabs,
  fill = false,
  children,
}: DashboardShellProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [])

  const ageSeconds =
    dataUpdatedAt && dataUpdatedAt > 0 ? Math.max(0, Math.round((now - dataUpdatedAt) / 1000)) : null

  return (
    <div className="page-fill gap-2 px-3 py-2">
      {/* Title bar */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="min-w-0">
          {kicker && <div className="terminal-kicker !text-[9px]">{kicker}</div>}
          <h1 className="truncate text-[15px] font-semibold leading-tight text-stone-100">
            {title}
          </h1>
          {subtitle && <p className="truncate text-[11px] text-stone-500">{subtitle}</p>}
        </div>

        {filters && <div className="flex items-center gap-1.5">{filters}</div>}

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <DashboardCustomizeControl />
          {partition && (
            <span className="rounded border border-amber-400/15 bg-white/[0.02] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-stone-500">
              {partition}
            </span>
          )}
          <span
            className={clsx(
              'flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest',
              ageSeconds == null
                ? 'border-amber-400/10 bg-white/[0.02] text-stone-600'
                : 'border-emerald-400/25 bg-emerald-400/[0.06] text-emerald-300',
            )}
            title={
              dataUpdatedAt
                ? `Last updated ${clockStamp(dataUpdatedAt)} — refreshes every 30s`
                : 'Waiting for the first response'
            }
          >
            <span
              className={clsx(
                'status-dot',
                isFetching ? 'bg-amber-400' : ageSeconds == null ? 'bg-stone-600' : 'bg-emerald-400',
              )}
            />
            Live · {ageSeconds == null ? '--' : `${ageSeconds}s`}
          </span>
          {dataUpdatedAt ? (
            <span className="font-mono text-[9px] text-stone-600">
              Updated {clockStamp(dataUpdatedAt)}
            </span>
          ) : null}
          {exportTemplate && (
            <ExportIcons template={exportTemplate} filters={exportFilters ?? {}} />
          )}
        </div>
      </div>

      {/* A failed board must say so at the top, not leave every panel to
          individually imply an empty firm. */}
      {error ? (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 rounded-md border border-red-500/25 bg-red-500/[0.04] px-3 py-2">
          <span className="text-[11px] font-medium text-red-300">
            This board could not be loaded — the figures below are not current.
          </span>
          <span className="text-[11px] text-stone-400">{panelErrorMessage(error)}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="ml-auto flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
            >
              <RotateCw size={10} /> Retry
            </button>
          )}
        </div>
      ) : null}

      {tabs && <div className="flex-shrink-0">{tabs}</div>}

      <div
        className={clsx(
          'min-h-0 flex-1',
          fill ? 'flex flex-col overflow-hidden' : 'overflow-y-auto scrollbar-thin',
        )}
      >
        {children}
      </div>

      <DrillDownDialog />
    </div>
  )
}
